/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { getData } from '../internalDatabase.ts';
import SDFParser from '../lib/io/parsers/SDFParser';
import CIFParser from '../lib/io/parsers/CIFParser';
import PDBParser from '../lib/io/parsers/PDBParser';
import PubChemParser from '../lib/io/parsers/PubChemParser';
import XYZParser from '../lib/io/parsers/XYZParser';
import MOL2Parser from '../lib/io/parsers/MOL2Parser';
import { MoleculeInterface } from '../types.ts';
import { useStore } from '../stores/common.ts';
import { MolecularProperties, MolecularStructure } from '../models/MolecularProperties.ts';
import { Molecule } from '../models/Molecule.ts';
import Complex from '../lib/chem/Complex';
import MoleculeJS from '../lib/chem/Molecule';
import Element from '../lib/chem/Element';
import AtomJS from '../lib/chem/Atom';
import { Atom } from '../models/Atom.ts';
import { applyBeadModelToMolecule, beadComposition, decorateBeadComplex } from '../models/beadModel.ts';
import { Vector3 } from 'three';
import { ModelUtil } from '../models/ModelUtil.ts';
import { RadialBond } from '../models/RadialBond.ts';
import { VdwBond } from '../models/VdwBond.ts';
import { MolecularViewerStyle } from './displayOptions.ts';
import { Util } from '../Util.ts';
import { applyAtomChargesToAtoms, rehydratePartialChargesFromSdf } from '../gasteigerCharges.ts';
import { complexToSdf } from '../autoBondSdf.ts';
import { rehydrateComputedPropertiesFromSdf } from '../computedProperties.ts';
import { setMessage } from '../helpers.tsx';
import i18n from '../i18n/i18n.ts';
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { loadPdbCache, savePdbCache } from './pdbCache.ts';
import { pdbIdFromUrl } from './rcsbFetch.ts';

export const isCrystal = (name: string) => {
  return name === 'Gold' || name === 'Silver' || name === 'Iron' || name === 'NaCl' || name === 'CsCl';
};

export const isSkinny = (style: MolecularViewerStyle) => {
  return (
    style === MolecularViewerStyle.BallAndStick ||
    style === MolecularViewerStyle.Stick ||
    style === MolecularViewerStyle.Wireframe ||
    style === MolecularViewerStyle.Cartoon ||
    style === MolecularViewerStyle.Trace ||
    style === MolecularViewerStyle.Tube ||
    style === MolecularViewerStyle.AtomIndex
  );
};

export const isCartoon = (style: MolecularViewerStyle) => {
  return (
    style === MolecularViewerStyle.Cartoon ||
    style === MolecularViewerStyle.Trace ||
    style === MolecularViewerStyle.Tube
  );
};

export const generateVdwLines = (molecules: Molecule[], maximumRelativeDistanceSquared: number) => {
  const bonds: VdwBond[] = [];
  // intermolecular
  const n = molecules.length;
  for (let i = 0; i < n - 1; i++) {
    const mi = molecules[i];
    for (const ai of mi.atoms) {
      const ei = Element.getByName(ai.elementSymbol);
      for (let j = i + 1; j < n; j++) {
        if (j === i) continue;
        const mj = molecules[j];
        for (const aj of mj.atoms) {
          const ej = Element.getByName(aj.elementSymbol);
          const dij = ei.radius + ej.radius;
          if (ai.distanceToSquared(aj) < maximumRelativeDistanceSquared * dij * dij) {
            bonds.push(new VdwBond(ai, aj));
          }
        }
      }
    }
  }
  // intramolecular
  // for (const m of molecules) {
  //   const atoms = m.atoms;
  //   const n=atoms.length;
  //   for(let i=0;i<n-1;i++){
  //     const ei = Element.getByName(atoms[i].elementSymbol);
  //     for(let j=i+1;j<n;j++){
  //       const ej = Element.getByName(atoms[j].elementSymbol);
  //       const dij = ei.radius + ej.radius;
  //       if (atoms[i].distanceToSquared(atoms[j]) < maximumRelativeDistanceSquared * dij * dij) {
  //         bonds.push(new VdwBond(atoms[i], atoms[j]));
  //       }
  //     }
  //   }
  // }
  return bonds;
};

export const joinComplexes = (molecules: Molecule[], complexes: Complex[]) => {
  let complex;
  if (complexes.length === 1) {
    complex = complexes[0];
  } else {
    complex = new Complex();
    complex.joinComplexes(complexes);
  }
  let i = 0;
  for (const [idx, mol] of molecules.entries()) {
    for (const a of mol.atoms) {
      const at = complex._atoms[i++];
      at.position = a.position;
      at.temperature = ModelUtil.convertToTemperatureFactor(a.getKineticEnergy());
    }
    const c = complexes[idx];
    if (c) {
      for (const chain of c._chains) {
        chain._name = 'MOL' + idx;
      }
    }
    const molecule = new MoleculeJS(complex, mol.name, idx + 1);
    molecule.residues = complexes[idx].getResidues();
    complex._molecules.push(molecule);
  }
  complex.finalize({ needAutoBonding: false, detectAromaticLoops: false, enableEditing: false });
  return complex;
};

export const storeMoleculeData = (molecule: MoleculeInterface, atoms: Atom[], radialBonds: RadialBond[]) => {
  // A coarse-grained molecule: the atoms were just parsed from its placeholder
  // SDF, so give them their bead parameters before anything reads a mass.
  if (molecule.beads) applyBeadModelToMolecule({ beads: molecule.beads, atoms });
  const properties = useStore.getState().getProvidedMolecularProperties(molecule.name);
  const generated = useStore.getState().projectState.generatedMolecularProperties[molecule.name];
  // Per-field fallback: many CSV rows have 'NA' (NaN) for bp / mp / density.
  // When that's the case but Joback has computed an estimate (stashed in
  // generatedMolecularProperties at import time), prefer the estimate over
  // a NaN that the gallery would otherwise render as "missing".
  const pickFinite = (csvValue: number | undefined, generatedValue: number | undefined): number | undefined =>
    Number.isFinite(csvValue) ? csvValue : Number.isFinite(generatedValue) ? generatedValue : csvValue;
  if (properties) {
    useStore.getState().setMolecularProperties(molecule.name, {
      atomCount: atoms.length,
      bondCount: radialBonds.length,
      molecularMass: properties.molecularMass,
      logP: properties.logP,
      hydrogenBondDonorCount: properties.hydrogenBondDonorCount,
      hydrogenBondAcceptorCount: properties.hydrogenBondAcceptorCount,
      rotatableBondCount: properties.rotatableBondCount,
      polarSurfaceArea: properties.polarSurfaceArea,
      heavyAtomCount: properties.heavyAtomCount,
      complexity: properties.complexity,
      density: pickFinite(properties.density, generated?.density),
      boilingPoint: pickFinite(properties.boilingPoint, generated?.boilingPoint),
      meltingPoint: pickFinite(properties.meltingPoint, generated?.meltingPoint),
      formula: properties.formula,
      // CSV rows never carry Fsp3 / ring counts / QED — pull them straight
      // from the computed (Joback + structural) record that
      // computeImportedProperties stashed for this molecule.
      fsp3: pickFinite(properties.fsp3, generated?.fsp3),
      ringCount: pickFinite(properties.ringCount, generated?.ringCount),
      aromaticRingCount: pickFinite(properties.aromaticRingCount, generated?.aromaticRingCount),
      qed: pickFinite(properties.qed, generated?.qed),
    } as MolecularProperties);
  } else {
    const p = useStore.getState().projectState.generatedMolecularProperties[molecule.name];
    if (p) {
      useStore.getState().setMolecularProperties(molecule.name, {
        atomCount: atoms.length,
        bondCount: radialBonds.length,
        molecularMass: p.molecularMass,
        heavyAtomCount: p.heavyAtomCount,
        formula: p.formula ?? generateFormula(atoms),
        logP: p.logP,
        hydrogenBondDonorCount: p.hydrogenBondDonorCount,
        hydrogenBondAcceptorCount: p.hydrogenBondAcceptorCount,
        rotatableBondCount: p.rotatableBondCount,
        polarSurfaceArea: p.polarSurfaceArea,
        complexity: p.complexity,
        density: p.density,
        boilingPoint: p.boilingPoint,
        meltingPoint: p.meltingPoint,
        description: p.description,
        fsp3: p.fsp3,
        ringCount: p.ringCount,
        aromaticRingCount: p.aromaticRingCount,
        qed: p.qed,
      } as MolecularProperties);
    }
  }
  useStore.getState().setMolecularStructure(molecule.name, { atoms, radialBonds } as MolecularStructure);
};

export const generateFormula = (atoms: Atom[]): string => {
  const symbols: Map<string, number> = new Map();
  for (const a of atoms) {
    const s = Util.capitalizeFirstLetter(a.elementSymbol);
    const n = symbols.get(s);
    symbols.set(s, (n ?? 0) + 1);
  }
  let formula: string = '';
  for (const a of symbols.keys()) {
    formula += a;
    const n = symbols.get(a);
    if (n && n > 1) formula += n;
  }
  return Util.getSubscriptNumber(formula);
};

/** The formula line of a molecule record: its bead composition when coarse-grained, else from its atoms. */
export const formulaOrComposition = (molecule: Pick<MoleculeInterface, 'beads'>, atoms: AtomJS[]): string =>
  molecule.beads ? beadComposition(molecule.beads) : generateFormulaFromAtomJS(atoms);

export const generateFormulaFromAtomJS = (atoms: AtomJS[]): string => {
  const symbols: Map<string, number> = new Map();
  for (const a of atoms) {
    const s = Util.capitalizeFirstLetter(a.element.name);
    const n = symbols.get(s);
    symbols.set(s, (n ?? 0) + 1);
  }
  let formula: string = '';
  for (const a of symbols.keys()) {
    formula += a;
    const n = symbols.get(a);
    if (n && n > 1) formula += n;
  }
  return Util.getSubscriptNumber(formula);
};

export interface LoadMoleculeOptions {
  // Which failure toast to show when parsing molecule.data throws. 'molecule'
  // (default) uses the generation-oriented copy ("the generated result is not
  // accepted"), which is right for the molecule/crystal generate + gallery
  // paths. 'bio' uses structure-render copy for the Protein / DNA-RNA labs,
  // where nothing was generated — a fetched PDB simply failed to render.
  errorContext?: 'molecule' | 'bio';
}

// One fetch per bundled asset URL. A lab of N copies of a built-in molecule (a
// saved project keeps only the url of an internal-DB molecule, not its text)
// fetched the same file N times — 255 argon atoms meant 255 requests on every
// load or rebuild. RCSB structures have their own cache (pdbCache) and skip this.
const bundledTextCache = new Map<string, Promise<string>>();

// The SDF (bonds included) an auto-bonded parse of a bundled asset produced,
// per URL. Auto-bonding costs 40–60 ms a parse even for a diatomic (the parse
// itself is ~5 ms), and a lab of N copies parsed the same file N times: the
// Diatomic Molecules example (75 copies of a few molecules) spent ~2.4 s here
// on every open. The first copy auto-bonds and publishes its SDF; the others
// wait for it and parse that SDF with its bonds as given. null = no SDF to
// share (the first parse failed, or kept PDB text) — each copy parses its own.
const derivedSdfCache = new Map<string, Promise<string | null>>();
const fetchBundledText = (url: string): Promise<string> => {
  let p = bundledTextCache.get(url);
  if (!p) {
    p = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
      return response.text();
    });
    p.catch(() => bundledTextCache.delete(url)); // a failed fetch may be retried
    bundledTextCache.set(url, p);
  }
  return p;
};

export const loadMolecule = (
  molecule: MoleculeInterface,
  processResult: (result: any, molecule?: Molecule) => void,
  removeMoleculeByName?: (name: string) => void,
  // Called when the parser rejects or the bundled URL fetch / parse fails.
  // Existing fire-and-forget callers (gallery, viewers) ignore this; the
  // generate pipelines pass an unblocker so their awaited promise wrapper
  // doesn't hang when the SDF is invalid.
  onError?: (err?: unknown) => void,
  opts?: LoadMoleculeOptions,
) => {
  // Sniff the format from the leading content — PDB files lead with HEADER /
  // ATOM / HETATM / CRYST1 / TITLE / COMPND lines; SDF files have a 3-line
  // header then a counts line. Lets a pre-fetched PDB (the RCSB path) route to
  // PDBParser even when shipped via `data` rather than `url`.
  const isPdb =
    !!molecule.data &&
    /^(HEADER|TITLE|COMPND|REMARK|ATOM|HETATM|CRYST1|MODEL|SEQRES)\s/.test(molecule.data.trimStart());
  // mmCIF (PDBx) source — the fallback format for RCSB entries with no legacy
  // PDB file (large / mmCIF-only structures, see rcsbFetch.cifUrl). The `.`
  // after atom_site is the mmCIF category.item syntax, which small-molecule
  // crystallographic CIF (`_atom_site_label`, underscore) never has — so this
  // targets macromolecular CIF only and can't hijack a crystal load. mmCIF
  // carries no bonds, so force autoBond to recover the backbone connectivity.
  const isCif = !!molecule.data && /_atom_site\./.test(molecule.data);
  // A residue-bearing source (PDB) must be parsed AS PDB. If a molecule carries
  // cached SDF `data` (which has no residue/chain records) but its bundled /
  // internal source is a .pdb, parse the PDB source instead — otherwise a
  // protein / nucleic acid (e.g. the DNA double helix) loads with a single
  // residue and the backbone styles (Cartoon / Trace / Tube), which are gated
  // on `multipleResidues`, disappear from the style menu. getData() resolves
  // the current bundled URL (a saved project's persisted url can be stale).
  const sourceUrl = getData(molecule.name)?.url ?? molecule.url ?? '';
  const preferPdbSource = sourceUrl.endsWith('.pdb') && !!molecule.data && !isPdb;

  if (molecule.data && !preferPdbSource) {
    const parser = isCif
      ? new CIFParser(molecule.data, { autoBond: true })
      : isPdb
        ? new PDBParser(molecule.data, { autoBond: !!molecule.autoBond })
        : new SDFParser(Util.correctSdf(molecule.data), { autoBond: !!molecule.autoBond });
    parser
      .parse()
      .then((result) => {
        // A coarse-grained molecule: draw its atoms as beads (radius, color)
        // before anything builds a visual from the parsed complex.
        if (molecule.beads) decorateBeadComplex(result, molecule.beads);
        processResult(result, molecule as Molecule);
      })
      .catch((err) => {
        console.error(err);
        console.error(molecule.data);
        const moleculeDataUpdated = usePrimitiveStore.getState().moleculeDataUpdated;
        const messageKey =
          opts?.errorContext === 'bio'
            ? 'message.BiomoleculeRenderFailed'
            : moleculeDataUpdated
              ? 'message.RevisionNotAccepted'
              : 'message.GeneratedResultNotAccepted';
        setMessage(
          'error',
          i18n.t(messageKey, { lng: useStore.getState().language }),
          opts?.errorContext === 'bio' ? 12 : moleculeDataUpdated ? 3 : 30,
        );
        if (removeMoleculeByName) {
          removeMoleculeByName(molecule.name);
        }
        usePrimitiveStore.getState().set((state) => {
          state.moleculeDataUpdated = false;
        });
        onError?.(err);
      });
  } else {
    // Look up the internal-DB record by name FIRST — bundled assets have
    // build-time hashed URLs (e.g. /aims/assets/protein_abc123.pdb) that
    // change every redeploy, so a saved project's persisted molecule.url
    // can be stale. getData() always returns the current bundled URL.
    // Fall back to the molecule's own `url` field only when the internal-
    // DB lookup misses — that's the RCSB-fetched path
    // (https://files.rcsb.org/download/1HVR.pdb), which has no internal
    // record and carries its URL directly on the molecule.
    const mol = getData(molecule.name) ?? molecule;
    if (!mol?.url) {
      onError?.();
      return;
    }
    // Cross-project cache for RCSB-fetched structures. A saved docking project
    // reopens with only `protein.url` — the receptor's inline PDB blob is
    // stripped on save (a full receptor would push the doc past Firestore's
    // 1 MiB limit). Without a cache, every open re-downloads the whole receptor
    // (e.g. 6HEB ≈ 400 KB) from RCSB and re-parses it. Serve it from the shared
    // pdbCache — the same LRU store the biomolecule loader fills via
    // fetchPdbText — so only the first open on a device pays the round-trip.
    // Scoped to RCSB structure URLs (pdbIdFromUrl matches `.pdb`/`.cif`);
    // bundled/local assets are fast and keyed by a hashed URL, so leave them be.
    // The cache key mirrors fetchPdbText's convention (`.cif` suffixed) so both
    // loaders share entries.
    const rcsbId = pdbIdFromUrl(mol.url);
    const pdbCacheKey = rcsbId ? (mol.url.endsWith('.cif') ? `${rcsbId}.cif` : rcsbId) : null;
    const cachedPdbText = pdbCacheKey ? loadPdbCache(pdbCacheKey) : null;
    const textPromise = cachedPdbText
      ? Promise.resolve(cachedPdbText)
      : pdbCacheKey
        ? fetch(mol.url)
            .then((response) => response.text())
            .then((text) => {
              savePdbCache(pdbCacheKey, text);
              return text;
            })
        : fetchBundledText(mol.url);
    // One auto-bonded parse per bundled file (see derivedSdfCache): the first
    // copy registers the slot and fills it after its parse; later copies wait.
    const derivedKey = mol.url;
    const shareable = !pdbCacheKey && !isCrystal(molecule.name); // the auto-bonded case
    const earlier = shareable ? derivedSdfCache.get(derivedKey) : undefined;
    let settleDerived: ((sdf: string | null) => void) | undefined;
    if (shareable && !earlier) {
      derivedSdfCache.set(
        derivedKey,
        new Promise<string | null>((resolve) => {
          settleDerived = (sdf) => {
            settleDerived = undefined;
            if (!sdf) derivedSdfCache.delete(derivedKey); // a later load tries again
            resolve(sdf);
          };
        }),
      );
    }
    let parserStarted = false;
    const sourcePromise: Promise<{ text: string; derived: boolean }> = earlier
      ? earlier.then((sdf) =>
          sdf ? { text: sdf, derived: true } : textPromise.then((text) => ({ text, derived: false })),
        )
      : textPromise.then((text) => ({ text, derived: false }));
    sourcePromise
      .then(({ text, derived }) => {
        let url = mol.url;
        if (!url) {
          onError?.();
          return;
        }
        if (url.includes('?')) {
          // sometimes the url has an appendix (not sure who adds it)
          url = url.substring(0, url.indexOf('?'));
        }
        let parser = null;
        const options = { autoBond: !isCrystal(molecule.name) };
        if (derived) parser = new SDFParser(text, { autoBond: false });
        else if (url.endsWith('.sdf')) parser = new SDFParser(text, options);
        else if (url.endsWith('.cif')) parser = new CIFParser(text, options);
        else if (url.endsWith('.pdb')) parser = new PDBParser(text, options);
        else if (url.endsWith('.pcj')) parser = new PubChemParser(text, options);
        else if (url.endsWith('.xyz')) parser = new XYZParser(text, options);
        else if (url.endsWith('.mol2')) parser = new MOL2Parser(text, options);
        if (!parser) {
          onError?.();
          return;
        }
        parserStarted = true;
        parser
          .parse()
          .then((result) => {
            // Internal-database molecules ship as just `{ url, name }` —
            // historically `molecule.data` was never populated, which
            // hid the popup's menu and skipped the partial-charges
            // rehydrate. After parsing, cache a text representation on the
            // in-store molecule so the menu shows up, Gasteiger runs, and
            // downstream tooling has something to parse.
            //
            // For SMALL molecules we re-emit V2000 SDF (uniform across all
            // source formats, carries the partial-charge tag). But for a
            // MULTI-RESIDUE biomolecule (protein / nucleic acid such as the
            // DNA double helix) the cache MUST stay the original PDB text:
            // SDF has no residue/chain records, so an SDF round-trip
            // collapses the structure to a single residue. The chamber re-
            // parses molecule.data when the molecule is added to the lab
            // (lab.tsx addMoleculeAt → dynamicsViewer), and a 1-residue
            // complex fails the `multipleResidues` gate that the Cartoon /
            // Trace / Tube styles hang on — so those styles silently vanish
            // from the style menu (and could not draw a backbone anyway).
            // Keeping the PDB text lets the format sniff at the top of this
            // loader route back to PDBParser and preserve the residues.
            const keepSourceText = !derived && url.endsWith('.pdb') && (result._residues?.length ?? 0) > 1;
            const sdf = keepSourceText || derived ? text : complexToSdf(result, '     AIMS imported');
            settleDerived?.(keepSourceText ? null : sdf || null);
            if (sdf) {
              // Try to mutate the passed-in molecule directly first —
              // dynamicsViewer's processResult (called immediately below) reads
              // `atom.charge` off this same reference. Going through
              // useStore.set() returns a *new* state object via Immer, but the
              // closure here still holds the pre-set reference, so an
              // Immer-mutated copy wouldn't help the dipole-current sampler.
              // Without this, internal-DB built-ins (alkanes, water, …) load
              // with q=0 everywhere and the IR spectrum is identically zero.
              // Order matters: rehydrate populates atomCharges from the SDF,
              // then applyAtomChargesToAtoms pushes those values down into
              // per-atom .charge fields.
              //
              // BUT — when this loader is called from the gallery (one viewer
              // per card), `molecule` is a reference straight out of
              // useStore(state.projectState.molecules), which Immer freezes.
              // The direct assignment then throws "Cannot add property data,
              // object is not extensible" and the .then callback aborts
              // before the store-side update below ever runs — leaving the
              // card permanently empty. Guard the direct mutation with
              // Object.isExtensible so the gallery path falls through to the
              // store-side update, which is the only one it needs anyway
              // (the gallery doesn't drive MD; the IR-sampler concern is
              // dynamicsViewer-specific).
              const mol = molecule as Molecule;
              if (Object.isExtensible(mol)) {
                if (!mol.data) mol.data = sdf;
                // The partial-charge / computed-property rehydrate steps parse
                // their input as SDF; skip them when we cached PDB text (a
                // freshly-fetched biomolecule carries no charge tag anyway, and
                // these descriptors are hidden for biomolecules).
                if (!keepSourceText) {
                  rehydratePartialChargesFromSdf([mol]);
                  applyAtomChargesToAtoms([mol]);
                }
              }
              // Only the first load of this molecule has anything to cache on the
              // gallery copy: once it holds data, the rehydrates below already ran
              // (here or at merge time). Skipping the no-op store write matters for a
              // lab of N copies — each write is a full store update, N per load.
              const galleryCopy = useStore.getState().projectState.molecules.find((m) => m.name === molecule.name);
              if (galleryCopy && !galleryCopy.data)
                useStore.getState().set((state) => {
                  for (const m of state.projectState.molecules) {
                    if (m.name === molecule.name) {
                      if (!m.data) m.data = sdf;
                      if (keepSourceText) break;
                      rehydratePartialChargesFromSdf([m]);
                      // Populate computed structural / descriptor / drug-
                      // likeness / Fsp³ / ring counts / QED for internal-DB
                      // molecules that landed in the project without a
                      // generatedMolecularProperties entry. The merge-time
                      // hydrate covers projects whose SDF was already cached
                      // at save time; this covers the first-time-load case
                      // where the SDF was just now fetched from the bundled
                      // URL.
                      rehydrateComputedPropertiesFromSdf([m], state.projectState.generatedMolecularProperties);
                      break;
                    }
                  }
                });
            }
            if (molecule) {
              processResult(result, molecule as Molecule);
            } else {
              processResult(result);
            }
          })
          .catch((err) => {
            console.error(err);
            onError?.(err);
          })
          .finally(() => settleDerived?.(null)); // no-op once the SDF was published
      })
      .catch((err) => {
        console.error(err);
        onError?.(err);
      })
      .finally(() => {
        // An early return (no url, unknown format) never reaches the parse.
        if (!parserStarted) settleDerived?.(null);
      });
  }
};

// Shared headless parse → storeMoleculeData path (no UI). storeMoleculeData fills
// BOTH molecularPropertiesMap and molecularStructureMap, so the two "ensure"
// wrappers below both prime both maps — they differ only in which map they check
// to decide the work is already done. Async: the parse resolves on a promise, so
// callers that need the result immediately must poll the relevant map.
const parseAndStoreMolecule = (molecule: MoleculeInterface): void => {
  loadMolecule(
    molecule,
    (result: any) => {
      const rawAtoms = result?._atoms;
      if (!rawAtoms || rawAtoms.length === 0) return;
      const atoms: Atom[] = [];
      for (let i = 0; i < rawAtoms.length; i++) {
        const a = rawAtoms[i] as AtomJS;
        atoms.push(new Atom(a.index, a.element.name, new Vector3(a.position.x, a.position.y, a.position.z)));
      }
      const radialBonds: RadialBond[] = [];
      for (const b of result._bonds) {
        radialBonds.push(new RadialBond(atoms[b._left.index], atoms[b._right.index]));
      }
      storeMoleculeData(molecule, atoms, radialBonds);
    },
    undefined,
    // Parse errors are already surfaced by the card-render path; stay silent here.
    () => {},
  );
};

// Headlessly populate molecularPropertiesMap for a gallery molecule WITHOUT
// rendering its card. The gallery property charts (parallel-coordinates,
// radar, scatter) read molecularPropertiesMap, which is normally filled as a
// side effect of a card's 3D viewer mounting (galleryViewer.processResult).
// In the gallery's CHAT mode the cards are lazy-mounted, so off-screen
// molecules never get an entry and silently drop out of the graphs. This
// helper runs the same parse → storeMoleculeData path with no UI, so every
// molecule is charted regardless of card visibility. No-ops if the molecule
// already has properties or has no data.
export const ensureMolecularProperties = (molecule: MoleculeInterface): void => {
  if (!molecule?.data) return;
  if (useStore.getState().molecularPropertiesMap.has(molecule.name)) return;
  parseAndStoreMolecule(molecule);
};

// Headlessly populate molecularStructureMap for a gallery molecule WITHOUT
// rendering its card, so loadMoleculeToLab can place it even in the gallery's
// CHAT view — where cards lazy-mount, so an off-screen molecule has no parsed
// structure and the placement effect in lab.tsx silently no-ops (surfacing as a
// misleading lab_not_ready). Same parse path as ensureMolecularProperties, keyed
// on the STRUCTURE map. Async (storeMoleculeData runs on the parser promise), so
// the caller must poll molecularStructureMap for the result. No-ops if the
// structure is already present or the molecule has no data.
export const ensureMolecularStructure = (molecule: MoleculeInterface): void => {
  if (!molecule?.data) return;
  if (useStore.getState().molecularStructureMap.get(molecule.name)?.atoms) return;
  parseAndStoreMolecule(molecule);
};
